/**
 * KAN-757: редактируемые поля строки CRM поверх CrmEditableCell: что показать,
 * какое значение дать редактору и как превратить ввод в правку строки.
 * KAN-760: этап воронки списком этапов доски, исход бейджем рядом.
 * KAN-765: у контакта иконка "Переписка" (модалка через useDialogParam) рядом
 * с "Карточка клиента".
 * KAN-857: у контакта Telegram-аватар клиента (без картинки инициалы).
 * KAN-865: ссылку "Карточка клиента" у контакта задаёт clientHref (приложение
 * менеджера ведёт в свою карточку); null - без ссылки (она уже есть рядом).
 * KAN-960: "Следующая связь" открывает модалку "Следующий контакт".
 */
import { Building2, MessageSquare, Plus, UserRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ClientAvatar } from '@/components/clients/ClientAvatar';
import { NextContactAction } from '@/components/clients/NextContactAction';
import { attentionNextContactTarget } from '@/components/dashboard/attentionFormat';
import { TelegramIconLink } from '@/components/clients/TelegramContacts';
import { useDialogParam } from '@/hooks/useDialogParam';
import { useCanOpenPath } from '@/hooks/useApiAccess';
import { useClientManagerRights } from '@/hooks/useClientManagerRights';
import { EMPTY_CELL, crmDialogTarget, crmRowToAttentionItem, safeTelegramLink, telegramLabel } from '@/lib/crmBoard';
import { NEXT_CONTACT_CREATE_TITLE, NEXT_CONTACT_LABEL } from '@/lib/nextContact';
import { normalizeTelegramUsername, telegramUsernameEdit } from '@/lib/crmRowEdit';
import type { ManagerBrief } from '@/types/manager';
import type { CrmAttribute, CrmFunnelStage, CrmRow, CrmRowPatch } from '@/types/crm';
import { CrmOutcomeBadge, CrmStageBadge, CrmStageCell, NextContactCell, TextCell } from './CrmCells';
import { CrmEditableCell, type CrmEditorOption } from './CrmEditableCell';

export type CrmRowSave = (patch: CrmRowPatch) => Promise<string | null>;

type TextField = 'phone' | 'clinic' | 'city';

const ICON_LINK = 'mt-0.5 shrink-0 text-gray-400 hover:text-blue-600';

export function CrmTextField({ row, field, label, save }: { row: CrmRow; field: TextField; label: string; save: CrmRowSave }) {
  return (
    <CrmEditableCell label={label} value={row[field] ?? ''} onSave={(v) => save({ [field]: v })}>
      {/* KAN-957: в таблице не выше 2 строк, полный текст в подсказке. */}
      <TextCell value={row[field]} lines={2} />
    </CrmEditableCell>
  );
}

/**
 * KAN-826: организация клиента. Правка пишет clinic: сервер находит или
 * создаёт организацию и привязывает клиента. При известной организации рядом
 * иконка-ссылка на её карточку (/companies/<id>).
 */
export function CrmCompanyField({ row, save }: { row: CrmRow; save: CrmRowSave }) {
  const company = row.client_company;
  const canOpen = useCanOpenPath(company ? `/companies/${company.id}` : '/companies');
  const label = company ? `Открыть организацию ${company.name || row.clinic || ''}`.trim() : '';
  return (
    <CrmEditableCell
      label="Организация"
      value={row.clinic ?? ''}
      onSave={(v) => save({ clinic: v })}
      addon={
        company && canOpen ? (
          <Link
            to={`/companies/${encodeURIComponent(company.id)}`}
            aria-label={label}
            title={label}
            className={ICON_LINK}
          >
            <Building2 className="h-3.5 w-3.5" />
          </Link>
        ) : undefined
      }
    >
      <TextCell value={row.clinic || company?.name} lines={2} />
    </CrmEditableCell>
  );
}

const defaultClientHref = (clientId: string) => `/clients/${encodeURIComponent(clientId)}`;

export function CrmContactField({
  row,
  save,
  clientHref = defaultClientHref,
}: {
  row: CrmRow;
  save: CrmRowSave;
  /** KAN-865: ссылка на карточку клиента; null - ссылки нет. */
  clientHref?: ((clientId: string) => string) | null;
}) {
  const { openDialog } = useDialogParam();
  return (
    <CrmEditableCell
      label="Контакт"
      value={row.contact_name ?? ''}
      onSave={(v) => save({ contact_name: v })}
      addon={
        <span className="flex shrink-0 items-start gap-1">
          <button
            type="button"
            aria-label="Переписка"
            title="Переписка"
            className={ICON_LINK}
            onClick={(e) => {
              e.stopPropagation();
              openDialog(crmDialogTarget(row));
            }}
          >
            <MessageSquare className="h-3.5 w-3.5" />
          </button>
          {clientHref && (
            <Link to={clientHref(row.client_id)} aria-label="Карточка клиента" title="Карточка клиента" className={ICON_LINK}>
              <UserRound className="h-3.5 w-3.5" />
            </Link>
          )}
        </span>
      }
      leading={
        // An empty contact without a picture keeps the plain dash (no "?" initials).
        // KAN-864: outside the edit button, the avatar opens the picture large.
        (row.avatar_url || row.contact_name?.trim()) && (
          <ClientAvatar avatarUrl={row.avatar_url} name={row.contact_name ?? ''} size={24} />
        )
      }
    >
      <TextCell value={row.contact_name} />
    </CrmEditableCell>
  );
}

export function CrmTelegramField({ row, save }: { row: CrmRow; save: CrmRowSave }) {
  const current = normalizeTelegramUsername(row.telegram?.username ?? '');
  const href = safeTelegramLink(row.telegram?.link);
  return (
    <CrmEditableCell
      label="Telegram"
      value={current}
      onSave={async (v) => {
        const username = telegramUsernameEdit(v, row.telegram?.username);
        return username === null ? null : save({ telegram_username: username });
      }}
      addon={
        <TelegramIconLink href={href} className={ICON_LINK} />
      }
    >
      {telegramLabel(row.telegram?.username) ?? EMPTY_CELL}
    </CrmEditableCell>
  );
}

export function CrmManagerField({
  row,
  managers,
  save,
}: {
  row: CrmRow;
  managers: readonly ManagerBrief[];
  save: CrmRowSave;
}) {
  // KAN-828: обычный менеджер не меняет менеджера чужого клиента.
  const rights = useClientManagerRights(row.manager?.id);
  if (!rights.canChange) {
    return (
      <div>
        <TextCell value={row.manager?.name} />
        <p className="mt-0.5 text-xs text-gray-500" data-testid="crm-manager-locked">
          {rights.lockedHint}
        </p>
      </div>
    );
  }
  const options: CrmEditorOption[] = [
    { value: '', label: 'Не выбран' },
    ...managers.map((m) => ({ value: m.id, label: m.full_name })),
  ];
  // Менеджер в архиве не в списке активных: оставить его выбранным.
  if (row.manager && !managers.some((m) => m.id === row.manager?.id)) {
    options.push({ value: row.manager.id, label: row.manager.name });
  }
  return (
    <CrmEditableCell
      label="Менеджер"
      kind="select"
      value={row.manager?.id ?? ''}
      options={options}
      onSave={(v) => save({ manager_id: v || null })}
    >
      <TextCell value={row.manager?.name} />
    </CrmEditableCell>
  );
}

/**
 * KAN-840: дата показывается по времени организации (`timeZone`, по умолчанию Москва).
 * KAN-960: дата - ссылка на модалку "Следующий контакт" (новый контакт вместо текущего,
 * клиент берётся в работу); без даты - "+" ("Создать контакт"). Ручной правки даты через
 * PATCH строки больше нет: `save` не используется (подпись общая с остальными полями).
 */
export function CrmNextContactField({ row, timeZone }: { row: CrmRow; save?: CrmRowSave; timeZone?: string }) {
  // Вид и подпись шага на карточке: встреча открывается встречей, видно, что заменяется.
  const target = {
    ...attentionNextContactTarget(crmRowToAttentionItem(row)),
    followupAt: row.next_contact_at,
    followupType: row.next_contact_type || null,
    followupLabel: row.next_contact_label || null,
  };
  return (
    <NextContactAction
      target={target}
      trigger={(open) =>
        row.next_contact_at ? (
          <button
            type="button"
            onClick={open}
            className="text-left hover:underline"
            data-testid="crm-next-contact-open"
            title={NEXT_CONTACT_LABEL}
          >
            <NextContactCell value={row.next_contact_at} timeZone={timeZone} />
          </button>
        ) : (
          <button
            type="button"
            onClick={open}
            className="inline-flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-blue-600"
            title={NEXT_CONTACT_CREATE_TITLE}
            aria-label={NEXT_CONTACT_CREATE_TITLE}
            data-testid="crm-next-contact-create"
          >
            <Plus className="h-4 w-4" />
          </button>
        )
      }
      fallback={<NextContactCell value={row.next_contact_at} timeZone={timeZone} />}
    />
  );
}

export function CrmCommentField({ row, save }: { row: CrmRow; save: CrmRowSave }) {
  const text = row.comment?.trim() ? row.comment : null;
  return (
    <CrmEditableCell label="Комментарий" kind="textarea" value={row.comment ?? ''} onSave={(v) => save({ comment: v })}>
      {text ? (
        <span className="line-clamp-3 whitespace-pre-wrap break-words text-gray-700" title={text}>
          {text}
        </span>
      ) : (
        EMPTY_CELL
      )}
    </CrmEditableCell>
  );
}

/** KAN-783: подпись пустого значения в списках атрибутов (выбор его снимает). */
const ATTRIBUTE_EMPTY_OPTION: CrmEditorOption = { value: '', label: 'Не указано' };
const BOOL_OPTIONS: readonly CrmEditorOption[] = [
  ATTRIBUTE_EMPTY_OPTION,
  { value: 'да', label: 'да' },
  { value: 'нет', label: 'нет' },
];

/** Варианты атрибута; текущее значение вне списка остаётся выбранным. */
function attributeOptions(attribute: CrmAttribute, current: string): CrmEditorOption[] {
  if (attribute.type === 'bool') {
    return current && !BOOL_OPTIONS.some((o) => o.value === current)
      ? [...BOOL_OPTIONS, { value: current, label: current }]
      : [...BOOL_OPTIONS];
  }
  const options = [ATTRIBUTE_EMPTY_OPTION, ...attribute.options.map((value) => ({ value, label: value }))];
  if (current && !options.some((o) => o.value === current)) options.push({ value: current, label: current });
  return options;
}

/**
 * KAN-783: ячейка атрибута лида. Текст правится как комментарий (многострочно),
 * да/нет и выбор списком; пустое значение снимает атрибут
 * (патч {attributes: {[name]: ''}}).
 */
export function CrmAttributeField({
  row,
  attribute,
  save,
}: {
  row: CrmRow;
  attribute: CrmAttribute;
  save: CrmRowSave;
}) {
  const current = row.attributes?.[attribute.name] ?? '';
  const onSave = (value: string) => save({ attributes: { [attribute.name]: value } });
  if (attribute.type === 'text') {
    const text = current.trim() ? current : null;
    return (
      <CrmEditableCell label={attribute.name} kind="textarea" value={current} onSave={onSave}>
        {text ? (
          <span className="line-clamp-3 whitespace-pre-wrap break-words text-gray-700" title={text}>
            {text}
          </span>
        ) : (
          EMPTY_CELL
        )}
      </CrmEditableCell>
    );
  }
  return (
    <CrmEditableCell label={attribute.name} kind="select" value={current} options={attributeOptions(attribute, current)} onSave={onSave}>
      <TextCell value={current} lines={2} />
    </CrmEditableCell>
  );
}

/** Этапы по полю order; этап строки вне списка остаётся выбранным. */
function stageOptions(row: CrmRow, stages: readonly CrmFunnelStage[]): CrmEditorOption[] {
  const options: CrmEditorOption[] = [...stages]
    .sort((a, b) => a.order - b.order)
    .map((s) => ({ value: String(s.id), label: s.name }));
  if (!row.stage) return [{ value: '', label: 'Не выбран' }, ...options];
  if (!options.some((o) => o.value === String(row.stage?.id))) {
    options.push({ value: String(row.stage.id), label: row.stage.name });
  }
  return options;
}

export function CrmStageField({
  row,
  stages,
  save,
}: {
  row: CrmRow;
  stages: readonly CrmFunnelStage[];
  save: CrmRowSave;
}) {
  // Старый backend без списка этапов: только просмотр.
  if (stages.length === 0) return <CrmStageCell stage={row.stage} outcome={row.outcome} />;
  // Исход рядом и во время правки: вне ячейки-редактора.
  return (
    <div className="flex items-start gap-1">
      <div className="min-w-0 flex-1">
        <CrmEditableCell
          label="Этап"
          kind="select"
          value={row.stage ? String(row.stage.id) : ''}
          options={stageOptions(row, stages)}
          onSave={async (v) => (v ? save({ stage_id: Number(v) }) : null)}
        >
          <CrmStageBadge stage={row.stage} />
        </CrmEditableCell>
      </div>
      <CrmOutcomeBadge outcome={row.outcome} />
    </div>
  );
}
